import { css } from 'aphrodite';
import * as React from 'react';
import { toast } from 'sonner';
import { ISupportTicket } from '../../../../models/Support/ISupportTicket';
import { isValidEmail } from '../../../../models/utils/validationUtils';
import { useCreateTicketMutation } from '../../../../queries/Support/useSupportTicketMutations';
import { getApiErrorMessage } from '../../../../utils/Support/supportHelpers';
import { Button } from '../../../../web/components-v2/Button';
import {
	Dialog,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogPopup,
	DialogTitle,
} from '../../../../web/components-v2/Dialog';
import { buildAdminSupportTicketUrl } from '../../../utils';
import { supportSharedStyles as shared } from '../styles/supportSharedStyles';
import type { NewTicketDraftAccount } from './newTicketDraft';
import { NewTicketFields } from './NewTicketFields';
import { newTicketStyles as s } from './styles';
import { useNewTicketDraftSource, useNewTicketFormState } from './useNewTicketFormState';

export interface OutboundTicketModalProps {
	defaultAccount?: NewTicketDraftAccount;
	defaultMailboxId?: string;
	onClose: () => void;
	/** When provided the host owns post-create navigation; otherwise the success toast links to the ticket. */
	onCreated?: (ticket: ISupportTicket) => void;
}

/**
 * Start a conversation by emailing a customer. Collects no description: the ticket is created with no
 * threads and the email is written in the composer, so the customer gets a real first message.
 */
export const OutboundTicketModal: React.FC<OutboundTicketModalProps> = ({
	defaultAccount,
	defaultMailboxId,
	onClose,
	onCreated,
}) => {
	const draftSource = useNewTicketDraftSource('outbound', defaultAccount?.id);
	const form = useNewTicketFormState({ defaultAccount, defaultMailboxId, draftSource });
	const [error, setError] = React.useState('');
	const createTicket = useCreateTicketMutation();

	const recipientEmail = (form.contact?.email ?? '').trim();

	const handleSubmit = () => {
		const trimmedSubject = form.subject.trim();
		// Both slots reset up front: an early return otherwise leaves the previous submit's message
		// showing next to the new one.
		setError('');
		form.setContactError('');
		if (!form.mailboxId) {
			setError('Select a mailbox for the ticket.');
			return;
		}
		if (!recipientEmail) {
			form.setContactError('Choose the customer this email goes to.');
			return;
		}
		if (!isValidEmail(recipientEmail)) {
			form.setContactError("That email address isn't valid — pick another contact or enter one manually.");
			return;
		}
		if (!trimmedSubject) {
			setError('Subject is required.');
			return;
		}

		createTicket.mutate(
			{
				mode: 'outbound',
				subject: trimmedSubject,
				mailboxId: form.mailboxId,
				priority: form.priority,
				categoryId: form.validCategoryId || undefined,
				accountId: form.account?.id,
				contact: form.manualContact
					? { email: recipientEmail, name: form.contact?.name?.trim() || undefined }
					: { userId: form.contact?.userId, email: recipientEmail, name: form.contact?.name },
			},
			{
				onSuccess: ({ ticket, warnings }) => {
					form.clearDraft();
					const toastOptions = onCreated
						? undefined
						: {
								action: {
									label: 'View ticket',
									onClick: () => window.open(buildAdminSupportTicketUrl(ticket.id), '_blank'),
								},
							};
					if (warnings.length > 0) {
						toast.warning(
							`Ticket #${ticket.number} was created, but we couldn't ${warnings.join(' or ')}. You can finish that from the ticket view.`,
							toastOptions
						);
					} else {
						toast.success(`Ticket #${ticket.number} created — write your email`, toastOptions);
					}
					onCreated?.(ticket);
					onClose();
				},
				onError: err => setError(getApiErrorMessage(err, 'Failed to create the ticket. Please try again.')),
			}
		);
	};

	return (
		<Dialog
			open
			onOpenChange={open => {
				if (!open) onClose();
			}}
		>
			<DialogPopup styles={[s.popup]}>
				<DialogHeader>
					<DialogTitle>Email a Customer</DialogTitle>
					<DialogDescription>
						We'll create the ticket and open the composer. Nothing is sent until you press Send.
					</DialogDescription>
				</DialogHeader>
				<div className={css(shared.modalBody)}>
					<NewTicketFields
						form={form}
						contactLabel='Customer'
						contactHint='Required — this is who the email goes to.'
						subjectPlaceholder='What the email is about'
					/>
					{error && <div className={css(shared.modalErrorText)}>{error}</div>}
				</div>
				<DialogFooter>
					<Button variant='outline' size='sm' onClick={onClose}>
						Cancel
					</Button>
					<Button
						variant='primary'
						size='sm'
						onClick={handleSubmit}
						disabled={createTicket.isPending || form.mailboxesLoading || form.mailboxesFailed || form.hasNoAgentMailbox}
					>
						{createTicket.isPending ? 'Creating...' : 'Create & Compose'}
					</Button>
				</DialogFooter>
			</DialogPopup>
		</Dialog>
	);
};
